import { resolveKnobPadding } from './properties/v2/padding.js';
import { OPENHASP_CONTROL_TYPE } from './controlTypes.js';

function finiteNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
}

/**
 * Resolve the LVGL pad values used by the slider knob renderer.
 *
 * Legacy `knob.padding` is the total extra knob size and therefore maps to
 * half a pad on every side. Once a side is present it is a raw LVGL pad and
 * wins over the legacy fallback. The resolver is shared with Gauge's
 * padding editor so preview and UI use the same cascade semantics.
 */
export function resolveSliderKnobPadding(style = {}) {
    return resolveKnobPadding(style);
}

/**
 * Calculate the slider knob bounds using LVGL 7's position_knob() geometry.
 * Bounds use Konva's half-open convention: x/y is the first pixel and
 * width/height is the extent. `position` is the unpadded knob anchor on the
 * value axis (0..axisLength), exactly where LVGL anchors its base area.
 */
export function sliderKnobBounds({ position, width, height, horizontal = true, ...pads }) {
    const baseSize = Math.max(0, finiteNumber(horizontal ? height : width));
    // LVGL 7 uses `(knob_size >> 1)`, i.e. integer floor, for the leading
    // half. Keep that detail instead of using a floating-point half.
    const halfBaseSize = Math.floor(baseSize / 2);
    const anchor = finiteNumber(position);
    const left = finiteNumber(pads.left);
    const right = finiteNumber(pads.right);
    const top = finiteNumber(pads.top);
    const bottom = finiteNumber(pads.bottom);

    if (horizontal) {
        return {
            x: anchor - halfBaseSize - left,
            y: -top,
            width: baseSize + left + right,
            height: baseSize + top + bottom,
        };
    }

    return {
        x: -left,
        y: anchor - halfBaseSize - top,
        width: baseSize + left + right,
        height: baseSize + top + bottom,
    };
}

/**
 * Recover LVGL's unpadded value-axis position from a dragged Konva knob.
 * This is the inverse of sliderKnobBounds() and intentionally uses the
 * leading pad plus half of the base knob size, not the rendered knob centre;
 * that distinction matters for asymmetric pads.
 */
export function knobPositionFromBounds({ x = 0, y = 0, width, height, horizontal = true, ...pads }) {
    const baseSize = Math.max(0, finiteNumber(horizontal ? height : width));
    const halfBaseSize = Math.floor(baseSize / 2);
    const left = finiteNumber(pads.left);
    const top = finiteNumber(pads.top);
    return horizontal
        ? finiteNumber(x) + left + halfBaseSize
        : finiteNumber(y) + top + halfBaseSize;
}

export function clampTrackPosition(position, length) {
    const trackLength = Math.max(0, finiteNumber(length));
    return Math.max(0, Math.min(trackLength, finiteNumber(position)));
}

/**
 * Map the LVGL/openHASP value range onto the slider's native local bounds.
 * Vertical sliders run from max at the top to min at the bottom.
 */
export function valueToTrackPosition(value, min, max, length, vertical = false, type = OPENHASP_CONTROL_TYPE.NORMAL, padding) {
    const lower = finiteNumber(min);
    const upper = finiteNumber(max);
    const range = upper - lower;
    const objectLength = Math.max(0, finiteNumber(length));
    if (range === 0 || objectLength === 0) return 0;
    if (padding === undefined) {
        const fraction = (boundedValue(value, lower, upper) - lower) / range;
        const reverse = Number(type) === OPENHASP_CONTROL_TYPE.REVERSE
        const visualFraction = reverse ? (vertical ? fraction : 1 - fraction) : (vertical ? 1 - fraction : fraction)
        return visualFraction * objectLength
    }
    const leading = vertical ? finiteNumber(padding.top) : finiteNumber(padding.left);
    const trailing = vertical ? finiteNumber(padding.bottom) : finiteNumber(padding.right);
    const trackLength = Math.max(0, objectLength - leading - trailing);
    if (range === 0 || trackLength === 0) return 0;

    const bounded = boundedValue(value, lower, upper);
    const fraction = (bounded - lower) / range;
    const reverse = Number(type) === OPENHASP_CONTROL_TYPE.REVERSE
    const offset = Math.trunc(fraction * trackLength)
    if (!vertical) return reverse ? objectLength - 1 - trailing - offset : leading + offset
    return reverse ? leading - 1 + offset : objectLength - trailing - offset
}

/** Inverse of valueToTrackPosition(), used only while dragging the knob. */
export function trackPositionToValue(position, min, max, length, vertical = false, type = OPENHASP_CONTROL_TYPE.NORMAL, padding) {
    const lower = finiteNumber(min);
    const upper = finiteNumber(max);
    const objectLength = Math.max(0, finiteNumber(length));
    if (padding === undefined) {
        const fraction = clampTrackPosition(position, objectLength) / objectLength;
        const reverse = Number(type) === OPENHASP_CONTROL_TYPE.REVERSE
        const valueFraction = reverse ? (vertical ? fraction : 1 - fraction) : (vertical ? 1 - fraction : fraction)
        return lower + valueFraction * (upper - lower)
    }
    const leading = vertical ? finiteNumber(padding.top) : finiteNumber(padding.left);
    const trailing = vertical ? finiteNumber(padding.bottom) : finiteNumber(padding.right);
    const trackLength = Math.max(0, objectLength - leading - trailing);
    if (trackLength === 0 || upper === lower) return lower;

    const coordinate = finiteNumber(position);
    const reverse = Number(type) === OPENHASP_CONTROL_TYPE.REVERSE
    const offset = vertical
        ? (reverse ? coordinate - (leading - 1) : objectLength - trailing - coordinate)
        : (reverse ? objectLength - 1 - trailing - coordinate : coordinate - leading)
    const fraction = clampTrackPosition(offset, trackLength) / trackLength;
    const valueFraction = fraction
    return lower + valueFraction * (upper - lower)
}

function boundedValue(value, lower, upper) {
    return Math.max(Math.min(lower, upper), Math.min(Math.max(lower, upper), finiteNumber(value, lower)))
}
